/**
 * The app store — ADR-007 §4: the ONE stateful shell, and it is thin. It holds
 * the sitting's History (retained frozen Documents; the walk undo/redo move a
 * cursor over) and the shell's app state (mode, selection), and it exposes
 * `dispatch(command)` as the ONLY write into the work — there is no other edit
 * surface (ADR-006 §2.3). UI affordances resolve FROM this value and hold
 * nothing: v2's `updateUI` inverted — the store publishes, buttons subscribe,
 * and no DOM knowledge lives below this line.
 *
 * Two invariants this module exists to keep:
 *  - **Undo touches only the Document** (§3.3). Mode, selection, and any future
 *    view state never move as a side effect of undo/redo — they are the sitting,
 *    not the work.
 *  - **This edits a lineage the loader began** (§4's two doors). The store never
 *    imports `parseDocument` — it receives an already-loaded History and drives
 *    it through `applyCommand`/cursor moves. Engine-free (session-no-engine).
 * Depends on: kernel (History API + command vocabulary), shell-state.
 * Depended on by: main (the loader wires it), the shell surface, e2e probes.
 */
import {
  CommandRefusal,
  canRedo as historyCanRedo,
  canUndo as historyCanUndo,
  current,
  commandTargetBones,
  describeCommand,
  dispatch as pushCommand,
  isDirty as historyIsDirty,
  markSaved as markHistorySaved,
  parseCommand,
  redo as redoHistory,
  undo as undoHistory,
  type AnimatorDocument,
  type History,
} from '@harryhausen/kernel';
import {
  clearSelection,
  initialShell,
  select,
  selectClip as selectClipShell,
  previewLibraryClip as previewLibraryClipShell,
  setMode,
  type ShellMode,
  type ShellState,
} from './shell-state.js';
import {
  assertLayoutOffered,
  defaultWorkspaces,
  type PanelAvailability,
  type WorkspaceLayout,
  type Workspaces,
} from '../shell/workspace.js';

export interface StoreOptions {
  /**
   * The bones a pose command may name — Document vocabulary (the rig's bone
   * ids), NOT engine types. When given, a `pose.set`/`pose.clear` naming a bone
   * outside this set refuses ATOMICALLY (§8): History does not advance, so an
   * invalid edit can never commit and then trip the view projector after the
   * fact (Codex P1, #54). Omit it and pose targets are unchecked (unit tests).
   */
  readonly poseTargets?: ReadonlySet<string>;
  /**
   * The workspaces slice (ADR-015): per-mode layouts, view state like mode
   * and selection — undo never touches it. `initial` comes from the loader
   * (persisted-or-default, parse-gated LOUDLY there); `persist` is the
   * injected save (localStorage in the app, nothing in tests/headless).
   */
  readonly workspaces?: {
    readonly initial: Workspaces;
    readonly persist?: (workspaces: Workspaces) => void;
    /**
     * The registry's availability map. Given, setLayout applies the SAME law
     * the reload gate applies, so the shell can never hold a layout the next
     * boot would reject — which would reset every workspace preference to
     * punish the user for what we accepted (Codex P2, #63). Omit it and
     * layouts are unchecked, exactly as poseTargets above (unit tests).
     */
    readonly availability?: PanelAvailability;
  };
}

/**
 * Whether an affordance is offered, and — when it is not — WHY, so the surface
 * shows a disabled-with-reason and never a mysteriously dead button (No-Trap,
 * ADR-007 §3.3).
 */
export interface Affordance {
  readonly ok: boolean;
  readonly reason?: string;
}

export interface AppStore {
  /** The Document the sitting is looking at right now (the History cursor). */
  getDocument(): AnimatorDocument;
  getHistory(): History;
  getShell(): ShellState;

  /**
   * The one write into the work (§2.3). Gated + applied by the kernel; a
   * refused command throws and leaves the store untouched (atomic, §8) — no
   * subscriber is notified, because nothing changed.
   */
  dispatch(command: unknown): void;
  /**
   * ADR-007 §4's OTHER door. `parseDocument` begins a lineage; `applyCommand`
   * edits inside one — and until a subject could be replaced at runtime, the
   * loading door existed only at boot, so the store never exposed it.
   * Importing a new specimen is a LOAD, not an edit: it starts a fresh lineage
   * over a Document with a different rig.
   *
   * Consequences, all deliberate. **Undo does not cross a load** — you do not
   * undo an import, you import again; canUndo() therefore reports the No-Trap
   * reason for a fresh sitting. Selection and the staged clip are dropped,
   * because both address a Document that no longer exists. Mode and the
   * workspace layout SURVIVE: they are preferences about the tool, not facts
   * about the subject.
   */
  load(history: History, poseTargets?: ReadonlySet<string>): void;
  /**
   * The rig on stage changed INSIDE a lineage — a tail grown or withdrawn, an
   * elbow rebuilt, undo/redo of either (the armature-changing verbs re-project
   * through `runDocument` without a load). The bone gate must guard the rig
   * actually on stage, so every such remount hands its bone set here: the set
   * captured at the last `load` would refuse a new `Tail1_graft` and accept a
   * withdrawn one (Codex/Luna P1, #180). History, selection and the
   * announcement are untouched — this is not a Document change, and nothing
   * is notified.
   */
  setPoseTargets(poseTargets: ReadonlySet<string>): void;
  /**
   * Which lineage the store is in: 0 at boot, +1 per `load`. Session state
   * that belongs to a Document and not to the tool — hero take bytes, say —
   * keys on this and is dropped when it changes, since undo never crosses a
   * load (Codex P1, #154).
   */
  lineage(): number;
  undo(): void;
  redo(): void;
  canUndo(): Affordance;
  canRedo(): Affordance;
  isDirty(): boolean;
  /**
   * The announcement (§3.3): what the last state change DID — "posed …" on
   * dispatch, "undid: posed …" on undo, "redid: posed …" on redo — because
   * undo's announcement must name the command it removed, not whatever older
   * command the cursor now rests on (Codex P2, #54 round 2). `null` until the
   * sitting first changes state. Derived from the command's own data via
   * describeCommand, so it can never drift from what happened.
   */
  lastAction(): string | null;
  markSaved(): void;

  setMode(mode: ShellMode): void;
  select(boneIds: readonly string[]): void;
  clearSelection(): void;
  /**
   * Put an authored clip on stage by NAME, or null for the default
   * presentation (§13.4). A name not in the current Document refuses listing
   * what exists (§8, the take-by-name idiom) — but an ALREADY-selected name
   * later orphaned by undo is view state and simply resolves to nothing at
   * projection time (§3.3: undo never writes the shell).
   */
  selectClip(name: string | null): void;
  /**
   * Preview a LIBRARY clip on the subject by World's clipId, or null to stop
   * (ADR-033 phase 4). View state: undo never moves it; a new Document
   * drops it with the selection. The caller has already fetched and bound
   * the clip — the store only says which one the stage should play.
   */
  previewLibraryClip(clipId: string | null): void;

  /** The per-mode workspace map (ADR-015) — view state, untouched by undo. */
  getWorkspaces(): Workspaces;
  /** The CURRENT mode's layout — what the workspace shell renders. */
  getLayout(): WorkspaceLayout;
  /**
   * Replace the current mode's layout (callers apply the pure workspace
   * transitions and hand back the result). Identity is the no-op signal;
   * a change persists through the injected saver and notifies.
   */
  setLayout(layout: WorkspaceLayout): void;

  /** Subscribe to any state change; returns an unsubscribe. */
  subscribe(listener: () => void): () => void;
}

/**
 * Build the store over a loaded History (the loader made it via `createHistory`
 * — the two-doors split). Shell defaults to a fresh sitting.
 */
export function createStore(
  history: History,
  shell: ShellState = initialShell(),
  options: StoreOptions = {},
): AppStore {
  let currentHistory = history;
  let currentShell = shell;
  const listeners = new Set<() => void>();
  // Per-SUBJECT, so `load` replaces them: a new specimen has different bones,
  // and the phantom-bone gate must guard the rig actually on stage.
  let poseTargets = options.poseTargets;
  let currentWorkspaces: Workspaces = options.workspaces?.initial ?? defaultWorkspaces({});
  const persistWorkspaces = options.workspaces?.persist;
  const layoutAvailability = options.workspaces?.availability;
  let lineageCount = 0;
  // The lineage's loaded floor: if trim ever advances baseDocument past this,
  // cursor 0 is a retained checkpoint, NOT the start of the sitting — and the
  // No-Trap reason must say so (§3.3; Codex P2, #54 round 2).
  let loadedBase = history.baseDocument;
  // What the last state change did (§3.3) — set by dispatch/undo/redo only.
  let lastAction: string | null = null;

  const notify = (): void => {
    for (const listener of listeners) listener();
  };

  return {
    getDocument: () => current(currentHistory),
    getHistory: () => currentHistory,
    getShell: () => currentShell,

    dispatch(command) {
      // Gate once up front (idempotent — pushCommand re-gates the envelope):
      // the parsed form both feeds the pose-target refusal and names the act
      // for the announcement.
      const parsed = parseCommand(command);
      // Refuse any edit naming a bone the rig does not have BEFORE committing —
      // the view projector's §8 refusal, pulled forward so it is atomic and a
      // bad edit can never enter History (Codex P1, #54). Uniform across the
      // vocabulary: pose.* and key.* both address rig bones, and a key on a
      // phantom bone is the same disease as a pose on one.
      // EVERY bone the command addresses, asked of the vocabulary itself. This
      // read `'bone' in parsed.params` — one bone — until clip.materialize
      // arrived carrying sixty tracks, none of which the gate could see.
      // Bound to a local so narrowing survives — `poseTargets` is now a `let`
      // (the load door replaces it), which defeats closure narrowing.
      const targets = poseTargets;
      const missing = targets === undefined ? [] : commandTargetBones(parsed).filter((b) => !targets.has(b));
      if (missing.length === 1) {
        throw new CommandRefusal(
          `target "${missing[0] as string}" is not a bone in this rig — nothing to edit there ` +
            `(§8: a command naming a bone the rig does not have refuses, atomically).`,
        );
      }
      if (missing.length > 1) {
        // Name them all: a materialize failing on one bone out of sixty is a
        // mapping story, and a refusal that shows only the first hides it.
        throw new CommandRefusal(
          `${missing.length} targets are not bones in this rig — nothing to edit there: ` +
            `${missing.join(', ')} (§8: refused atomically, before anything entered History).`,
        );
      }
      // pushCommand parses+applies and throws on refusal BEFORE returning, so
      // the assignments never run on a refused command — state stays put.
      currentHistory = pushCommand(currentHistory, command);
      lastAction = describeCommand(parsed);
      notify();
    },
    load(nextHistory, nextPoseTargets) {
      currentHistory = nextHistory;
      loadedBase = nextHistory.baseDocument;
      lineageCount += 1;
      poseTargets = nextPoseTargets;
      // Selection and the staged clip name a Document that is gone. Keeping
      // either would leave the chrome pointing at a bone this rig may not have
      // and the projector tracking a name nothing resolves — the phantom-bone
      // class, arriving through the load door instead of the edit door.
      currentShell = { ...currentShell, selection: [], activeClip: null, libraryClip: null };
      // The announcement described an act in a dead lineage.
      lastAction = null;
      notify();
    },
    setPoseTargets(nextPoseTargets) {
      poseTargets = nextPoseTargets;
    },
    lineage: () => lineageCount,
    undo() {
      // Name the entry being stepped back over BEFORE the cursor moves — the
      // announcement is what undo REMOVED, never the older command it lands on.
      const removed = currentHistory.entries[currentHistory.cursor - 1];
      const next = undoHistory(currentHistory);
      if (next === currentHistory) return; // nothing to undo — no-op, no notify
      currentHistory = next;
      lastAction = `undid: ${describeCommand((removed as NonNullable<typeof removed>).command)}`;
      notify();
    },
    redo() {
      const restored = currentHistory.entries[currentHistory.cursor];
      const next = redoHistory(currentHistory);
      if (next === currentHistory) return;
      currentHistory = next;
      lastAction = `redid: ${describeCommand((restored as NonNullable<typeof restored>).command)}`;
      notify();
    },
    canUndo() {
      if (historyCanUndo(currentHistory)) return { ok: true };
      // Cursor 0 is only "the start of the sitting" if trim never advanced the
      // base — otherwise it is the retained floor, and saying "start" would
      // hide that older edits were trimmed (§3.3's No-Trap, honestly worded).
      // The WORDS follow the CAUSE (Codex P2, #61 r4): "raise the cap" is only
      // an honest remedy for a count trim — a weight trim would ignore a
      // raised cap, so it reports the measured weight instead.
      if (currentHistory.baseDocument === loadedBase) {
        return { ok: false, reason: 'nothing to undo — you are at the start of the sitting' };
      }
      if (currentHistory.trimCause === 'bytes') {
        const newest = currentHistory.entries[currentHistory.entries.length - 1];
        const perEditKb = Math.max(1, Math.round((newest?.bytes ?? 0) / 1024));
        return {
          ok: false,
          reason:
            `history trimmed by weight — each retained edit of this document measures ~${perEditKb} KB ` +
            `and the memory budget holds ${currentHistory.entries.length} of them; older edits cannot be reached`,
        };
      }
      return {
        ok: false,
        reason:
          `history trimmed at ${currentHistory.capacity} — older edits were trimmed and cannot be ` +
          `reached (raise the cap in settings)`,
      };
    },
    canRedo() {
      return historyCanRedo(currentHistory)
        ? { ok: true }
        : { ok: false, reason: 'nothing to redo — this is the latest state' };
    },
    isDirty: () => historyIsDirty(currentHistory),
    lastAction: () => lastAction,
    markSaved() {
      currentHistory = markHistorySaved(currentHistory);
      notify();
    },

    setMode(mode) {
      const next = setMode(currentShell, mode);
      if (next === currentShell) return;
      currentShell = next;
      notify();
    },
    select(boneIds) {
      currentShell = select(currentShell, boneIds);
      notify();
    },
    selectClip(name) {
      if (name !== null) {
        const clips = current(currentHistory).clips;
        if (!clips.some((c) => c.name === name)) {
          throw new CommandRefusal(
            `no authored clip "${name}" — clips: ${clips.map((c) => c.name).join(', ') || '(none)'} (§8).`,
          );
        }
      }
      const next = selectClipShell(currentShell, name);
      if (next === currentShell) return;
      currentShell = next;
      notify();
    },
    previewLibraryClip(clipId) {
      const next = previewLibraryClipShell(currentShell, clipId);
      if (next === currentShell) return;
      currentShell = next;
      notify();
    },
    getWorkspaces: () => currentWorkspaces,
    getLayout: () => currentWorkspaces[currentShell.mode],
    setLayout(layout) {
      if (layout === currentWorkspaces[currentShell.mode]) return;
      // Refuses BEFORE the write, so a rejected layout leaves no trace —
      // atomic like the pose-target refusal (§8). The throw names the panel
      // and the modes that do offer it.
      if (layoutAvailability !== undefined) assertLayoutOffered(layout, currentShell.mode, layoutAvailability);
      currentWorkspaces = { ...currentWorkspaces, [currentShell.mode]: layout };
      persistWorkspaces?.(currentWorkspaces);
      notify();
    },
    clearSelection() {
      const next = clearSelection(currentShell);
      if (next === currentShell) return;
      currentShell = next;
      notify();
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
